import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import { SessionRecorder, TouchTracker, detectMultiTouch } from "@talent/client-telemetry";
import {
  BY_SHAPE,
  correctBin,
  passedRuleSwitch,
  perseverations,
  tokenColor,
  type RuleSwitchRound,
  DEMO_FORM,
} from "@talent/tasks";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  easeOutCubic,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime } from "./sound.js";
import { TOKEN_COLORS, drawToken } from "./token.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";

/**
 * The rule changed — sort each card into one of two bins.
 *
 * For a while the bins go by shape; then, with no announcement, by colour. The only way to notice
 * is the feedback: a card that would have been right a moment ago is now wrong.
 *
 * **The bins ARE the rule.** Under the shape rule each bin shows a plain grey shape; under the
 * colour rule, a plain colour with no shape. So a child can always see which comparison is being
 * asked for, and when the rule changes the bins visibly change with it.
 *
 * The first version showed a shape AND a colour on both bins and announced nothing, on the theory
 * that noticing the change unaided was the thing worth observing. That is the clinical card sort,
 * and the clinical card sort has a clinician beside the child telling them to sort. Unaided it was
 * not difficult, it was unreadable — two bins carrying two properties each leave no way to tell
 * which comparison is being asked. It gave up a measurement the product had already agreed not to
 * make: cognitive flexibility is descriptive-only in the Phase 0 triage.
 *
 * **The bins never move.** Left stays left for the whole round. If they swapped position the child
 * would face two changes at once — a new rule and a new layout — and a wrong tap would no longer
 * say which one they missed.
 */

export interface RuleSwitchTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateRound: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => RuleSwitchRound;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

interface Attempt {
  chosen: (0 | 1)[];
}

export async function runRuleSwitchTask(
  opts: RunOptions,
): Promise<RuleSwitchTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    // Deliberately constant. A backdrop that changed with the rule would announce the switch.
    backdrop.circle(w / 2, h * 0.45, Math.min(w, h) * 0.5).fill({ color: accent, alpha: 0.07 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(round: RuleSwitchRound): {
    attempt: Promise<Attempt>;
    simulateTap: (bin: 0 | 1) => void;
    relayout: () => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    const binLayer = new Container();
    const cardLayer = new Container();
    const pips = new Graphics();
    layer.addChild(binLayer, cardLayer, pips);

    const chosen: (0 | 1)[] = [];
    let index = 0;
    let accepting = true;
    let resolveAttempt: ((a: Attempt) => void) | null = null;

    const binGraphics: Graphics[] = [];
    const card = new Graphics();
    cardLayer.addChild(card);
    let binSize = 0;
    let cardSize = 0;
    let cardHome = { x: 0, y: 0 };
    const binHome: { x: number; y: number }[] = [];

    /** Progress through the round, as pips. A numeral would be text (v2 §15). */
    function drawPips(): void {
      const w = app.renderer.width;
      const total = round.cards.length;
      const step = Math.min(20, (w * 0.8) / total);
      const y = app.renderer.height - 30;
      let x = (w - (total - 1) * step) / 2;
      pips.clear();
      for (let i = 0; i < total; i++) {
        pips.circle(x, y, i < index ? 5.5 : 4).fill({
          color: i < index ? accent : COLOR.card,
          alpha: i < index ? 1 : 0.22,
        });
        x += step;
      }
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;

      binSize = Math.max(touchMin(), Math.min(w * 0.34, h * 0.22));
      cardSize = Math.min(w * 0.36, h * 0.24);
      const binY = h * 0.66;
      const gap = Math.max(24, w * 0.1);

      binHome.length = 0;
      binHome.push({ x: (w - gap) / 2 - binSize / 2, y: binY });
      binHome.push({ x: (w + gap) / 2 + binSize / 2, y: binY });

      binLayer.removeChildren().forEach((c) => c.destroy());
      binGraphics.length = 0;
      round.bins.forEach((token, i) => {
        const g = new Graphics();
        drawBin(g, token, binSize);
        // A ring, so a bin reads as a container to put something in rather than another card.
        g.roundRect(-binSize / 2 - 6, -binSize / 2 - 6, binSize + 12, binSize + 12, binSize * 0.3)
          .stroke({ width: 3, color: accent, alpha: 0.45 });
        const home = binHome[i] as { x: number; y: number };
        g.position.set(home.x, home.y);
        g.eventMode = "static";
        g.cursor = "pointer";
        const half = binSize / 2 + binSize * 0.14;
        g.hitArea = {
          contains: (x: number, y: number) => x >= -half && y >= -half && x <= half && y <= half,
        };
        g.on("pointertap", () => choose(i as 0 | 1));
        binGraphics.push(g);
        binLayer.addChild(g);
      });

      cardHome = { x: w / 2, y: h * 0.26 };
      card.position.set(cardHome.x, cardHome.y);
      drawCard();
      drawPips();
    }

    /**
     * A bin, showing ONLY the property the current rule sorts on.
     *
     * Grey under the shape rule so colour cannot be read into it, and a bare colour under the
     * colour rule so shape cannot. Showing both would put the child back in front of the ambiguity
     * this game was rebuilt to remove.
     */
    function drawBin(g: Graphics, token: number, size: number): void {
      const rule = round.rules[Math.min(index, round.rules.length - 1)] ?? round.rules[0];
      g.clear();
      g.roundRect(-size / 2, -size / 2, size, size, size * 0.26)
        .fill(mix(COLOR.stage, COLOR.card, 0.1))
        .stroke({ width: 3, color: accent, alpha: 0.45 });

      if (rule === BY_SHAPE) {
        // A neutral grey, so the bin says "shape" and says nothing at all about colour — which is
        // the property the child is being asked to ignore.
        const neutral = mix(COLOR.card, COLOR.stage, 0.35);
        drawToken(g, token, size * 0.72, "idle", accent, neutral);
      } else {
        // A plain blob of the colour, with no shape to read.
        g.circle(0, 0, size * 0.27).fill(TOKEN_COLORS[tokenColor(token)] ?? COLOR.sunny);
        g.circle(-size * 0.08, -size * 0.09, size * 0.06).fill({ color: 0xffffff, alpha: 0.4 });
      }
    }

    function drawCard(state: "idle" | "right" | "wrong" = "idle"): void {
      const token = round.cards[index];
      if (token === undefined) {
        card.clear();
        return;
      }
      drawToken(card, token, cardSize, state, accent);
    }

    /** Sends the current card to a bin and moves on. */
    function choose(bin: 0 | 1): void {
      if (!accepting) return;
      const token = round.cards[index];
      const rule = round.rules[index];
      if (token === undefined || rule === undefined) return;

      accepting = false;
      chosen.push(bin);
      const right = bin === correctBin(token, rule, round.bins);
      const target = binHome[bin] as { x: number; y: number };
      const binGraphic = binGraphics[bin];

      void (async () => {
        ripple(fx, target.x, target.y, accent, binSize);
        // The card travels to the bin the child picked, so the choice is visible as a movement
        // rather than only as a colour change.
        if (!prefersReducedMotion()) {
          const from = { x: card.position.x, y: card.position.y };
          await animate(240, (t) => {
            const e = easeOutCubic(t);
            card.position.set(from.x + (target.x - from.x) * e, from.y + (target.y - from.y) * e);
            card.scale.set(1 - 0.25 * e);
          });
        }

        if (right) {
          cellTone(index + 2);
          drawCard("right");
          burst(fx, target.x, target.y, [accent, lighten(accent, 0.4), COLOR.correct], 10);
          if (binGraphic) void pop(binGraphic, { scale: 1.12 });
        } else {
          missTone();
          drawCard("wrong");
          if (binGraphic) await nudge(binGraphic, 5);
          // The other bin lights, so the feedback says WHERE it belonged — the only channel
          // through which a child can discover that the rule has changed.
          const other = binGraphics[bin === 0 ? 1 : 0];
          const otherHome = binHome[bin === 0 ? 1 : 0];
          if (other && otherHome) {
            glow(glowLayer, otherHome.x, otherHome.y, binSize * 0.6, COLOR.correct, 620);
            await pop(other, { scale: 1.14 });
          }
        }

        await idle(right ? 320 : 620);

        const ruleBefore = round.rules[index];
        index++;
        drawPips();

        /**
         * The bins change with the rule, and that change is now the signal.
         *
         * Announced on purpose. The earlier design hid it, on the theory that noticing unaided was
         * the thing worth observing — which made the game unreadable and claimed a measurement the
         * product had already declined to make. A visible cue turns it into cued task switching:
         * easier, real, and playable by a child on their own.
         */
        const ruleNow = round.rules[index];
        if (ruleNow !== undefined && ruleBefore !== undefined && ruleNow !== ruleBefore) {
          binGraphics.forEach((g, i) => {
            const token = round.bins[i];
            if (token === undefined) return;
            drawBin(g, token, binSize);
            void pop(g, { scale: 1.18 });
          });
          // A distinct two-note rise, so the change is heard as well as seen.
          cellTone(2, { volume: 0.12 });
          cellTone(6, { delay: 0.1, volume: 0.12 });
          await idle(520);
        }

        if (index >= round.cards.length) {
          resolveAttempt?.({ chosen: [...chosen] });
          return;
        }

        // The next card arrives at the top. Reset before it is drawn, or it appears in the bin.
        card.position.set(cardHome.x, cardHome.y);
        card.scale.set(prefersReducedMotion() ? 1 : 0);
        drawCard();
        accepting = true;
        if (!prefersReducedMotion()) {
          await animate(240, (t) => card.scale.set(easeOutBack(t)));
        }
      })();
    }

    layout();
    /**
     * The pieces arrive rather than appearing.
     *
     * A board that is simply there reads as a diagram; one that is dealt out reads as a game being set
     * up for you — and it gives a child a moment to look before anything is expected of them. Staggered
     * by index so it is a sequence, and skipped entirely under `prefers-reduced-motion`, where the whole
     * point of the setting is that things do not fly around.
     */
    if (!prefersReducedMotion()) {
      binLayer.children.forEach((bin, i) => {
        bin.scale.set(0);
        void idle(80 + i * 90).then(() => animate(280, (t) => bin.scale.set(easeOutBack(t))));
      });
    }


    return {
      attempt: new Promise<Attempt>((resolve) => {
        resolveAttempt = resolve;
      }),
      simulateTap: choose,
      relayout: layout,
      cleanup: () => {
        accepting = false;
        layer.destroy({ children: true });
      },
    };
  }

  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctRounds = 0;
  let reason: RuleSwitchTaskResult["reason"] = "completed";
  let onResize: (() => void) | null = null;

  /**
   * A wordless demonstration of the SORTING, never of the switch.
   *
   * It shows two cards going into the right bins under one rule, and stops. Demonstrating a switch
   * would give away the one thing the task is about — that the rule can change without warning —
   * and a child told to expect it is no longer being observed noticing it.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateRound(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      await idle(800);
      // Only the cards before the first switch, so the demonstration never reveals one.
      const firstSwitch = demo.switchPoints[0] ?? demo.cards.length;
      for (let i = 0; i < Math.min(2, firstSwitch); i++) {
        if (opts.signal?.aborted) break;
        const token = demo.cards[i];
        const rule = demo.rules[i];
        if (token === undefined || rule === undefined) break;
        shown.simulateTap(correctBin(token, rule, demo.bins));
        await idle(1100);
      }
      if (!opts.signal?.aborted) {
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const round = opts.generateRound(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The cards, the bins and the rule in force for each — everything needed to re-score the
        // round from the record alone, without the client build that produced it.
        stimulus: round.cards,
        options: [round.bins as unknown as number[], round.rules as unknown as number[]],
        correctIndex: 0,
      });

      const start = performance.now();
      const trial = present(round);
      onResize = trial.relayout;
      app.renderer.on("resize", trial.relayout);

      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      app.renderer.off("resize", trial.relayout);
      onResize = null;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const passed = passedRuleSwitch(round, outcome.chosen);
      if (passed) correctRounds++;

      await recorder.record("trial_response", {
        trialIndex,
        response: outcome.chosen,
        correct: passed,
        responseMs: elapsed,
        // Cards still sorted by the OLD rule after a switch. The one number worth taking from this
        // task, and a count of actions rather than a latency — which is what keeps it inside PC-1.
        // Recorded and described; cognitive flexibility stays descriptive-only.
        corrections: perseverations(round, outcome.chosen),
      });

      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, passed);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }

      if (trialIndex > 0) {
        successChime();
        await idle(400);
      }
    }
  } catch {
    reason = "error";
  } finally {
    if (onResize) app.renderer.off("resize", onResize);
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctRounds / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctRounds,
    rawScore: trialIndex > 0 ? correctRounds / trialIndex : 0,
  };
}
